import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eraser, Layers, Search, Sparkles, TriangleAlert } from 'lucide-react';
import {
  DESCRICAO_ESTILO_VISUAL,
  ESTILOS_VISUAIS,
  MODELOS,
  ROTULO_CATEGORIA,
  ROTULO_ESTILO_VISUAL,
  construirDocumento,
  construirPaleta,
  criarConfigPadrao,
  modelosPorCategoria,
  quadrosDaLista,
  type CategoriaModelo,
  type ConfigPlanner,
  type EstiloVisual,
  type ModeloMiolo,
  type Pagina,
} from '@miolos/core';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PaginaImpressa } from '@/impressao/PaginaImpressa';
import { MolduraPrevia } from '@/impressao/MolduraPrevia';
import { urlDaImagem } from '@/lib/api';
import { useAcesso } from '@/auth/EstadoAcesso';
import { cn } from '@/lib/utils';

type Filtro = CategoriaModelo | 'TODAS';
type Vista = 'PAGINA' | 'CAPA';

/**
 * O tom de enfeite de cada categoria.
 *
 * Vem da **ordem** do catálogo (`ROTULO_CATEGORIA`), e não de uma lista escrita à
 * mão: categoria nova no motor ganha cor sozinha, e nenhuma fica sem tom por
 * esquecimento — o mesmo motivo pelo qual o menu do site é montado de uma lista
 * só. O ponto colorido da etiqueta e a borda do cartão saem daqui.
 */
const TONS_DA_CASA = [
  'var(--cor-alegre-4)',
  'var(--cor-alegre-3)',
  'var(--cor-alegre-1)',
  'var(--cor-alegre-2)',
  'var(--cor-alegre-5)',
];

const CATEGORIAS = Object.keys(ROTULO_CATEGORIA) as CategoriaModelo[];

function tomDaCategoria(categoria: CategoriaModelo): string {
  const indice = CATEGORIAS.indexOf(categoria);
  return TONS_DA_CASA[(indice < 0 ? 0 : indice) % TONS_DA_CASA.length]!;
}

/**
 * Gera as prévias uma a uma, só para os cartões que estão perto da tela.
 *
 * Montar o documento custa caro (~100 ms no motor e mais um tanto para o
 * navegador desenhar a página). Em fila e por proximidade, a página abre na
 * hora, a primeira prévia aparece em menos de um segundo e o resto entra
 * conforme a pessoa rola — sem gerar vinte e um documentos de uma vez.
 */
function useFilaDePrevias(ids: string[]): Record<string, boolean> {
  const [prontos, setProntos] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const pendentes = [...ids];
    let cancelado = false;
    let timer = 0;

    /** O cartão está na tela (ou a um passo dela)? */
    const perto = (id: string) => {
      const elemento = document.getElementById(`modelo-${id}`);
      if (!elemento) return false;
      const { top, bottom } = elemento.getBoundingClientRect();
      return bottom > -160 && top < window.innerHeight + 320;
    };

    const proximo = () => {
      if (cancelado || pendentes.length === 0) return;

      const indice = pendentes.findIndex(perto);
      if (indice >= 0) {
        const [id] = pendentes.splice(indice, 1);
        setProntos((atual) => ({ ...atual, [id]: true }));
        timer = window.setTimeout(proximo, 90);
        return;
      }

      // Nada perto da tela: espera a rolagem (com uma recheca de vez em quando).
      timer = window.setTimeout(proximo, 600);
    };

    const acordar = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(proximo, 30);
    };

    timer = window.setTimeout(proximo, 60);
    window.addEventListener('scroll', acordar, { passive: true });
    window.addEventListener('resize', acordar);

    return () => {
      cancelado = true;
      window.clearTimeout(timer);
      window.removeEventListener('scroll', acordar);
      window.removeEventListener('resize', acordar);
    };
  }, [ids]);

  return prontos;
}

/** Compara texto sem depender de acentos nem de maiúsculas. */
function semAcento(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/** Página que melhor representa o modelo: a primeira de dia, senão a primeira página. */
function indiceDaPagina(paginas: Pagina[]): number {
  const dia = paginas.findIndex((pagina) => pagina.tipo === 'DIA');
  if (dia >= 0) return dia;
  const caderno = paginas.findIndex((pagina) => pagina.tipo === 'CADERNO');
  return caderno >= 0 ? caderno : Math.min(1, paginas.length - 1);
}

interface AmostraDoModelo {
  pagina: Pagina;
  lado: 'ESQUERDO' | 'DIREITO';
}

interface AmostraProps {
  config: ConfigPlanner;
  paleta: ReturnType<typeof construirPaleta>;
  amostras: Record<Vista, AmostraDoModelo | null>;
}

/** Prévia real do modelo, na escala do cartão. */
function Amostra({ config, paleta, amostras }: AmostraProps) {
  const [vista, setVista] = useState<Vista>('PAGINA');
  const amostra = amostras[vista] ?? amostras.PAGINA ?? amostras.CAPA;

  if (!amostra) return null;

  return (
    <div className="space-y-2">
      <MolduraPrevia
        formato={amostra.pagina.formato}
        papel={config.papel}
        escalaMaxima={0.5}
        sombra={false}
      >
        <PaginaImpressa
          pagina={amostra.pagina}
          paleta={paleta}
          config={config}
          lado={amostra.lado}
          resolverImagem={urlDaImagem}
        />
      </MolduraPrevia>

      {/* A alternância só aparece quando há o que alternar: sem capa, é um botão só. */}
      {amostras.CAPA ? (
        <div className="flex items-center justify-center gap-1">
          {(
            [
              ['CAPA', 'Capa'],
              ['PAGINA', 'Página'],
            ] as const
          ).map(([valor, rotulo]) => (
            <button
              key={valor}
              type="button"
              onClick={() => setVista(valor)}
              disabled={!amostras[valor]}
              className={cn(
                'rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors disabled:opacity-40',
                vista === valor
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:border-primary/40',
              )}
            >
              {rotulo}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Cartão de um modelo: prévia, destaques e o caminho para personalizar. */
function CartaoDoModelo({
  modelo,
  bloqueado,
  gerar,
  estilo,
}: {
  modelo: ModeloMiolo;
  bloqueado: boolean;
  /** A fila da página libera a geração da prévia deste cartão. */
  gerar: boolean;
  /** Acabamento escolhido na página; nulo mantém o que o modelo já traz. */
  estilo: EstiloVisual | null;
}) {
  const pronto = useMemo(() => {
    if (!gerar) return null;
    const config = modelo.aplicar(criarConfigPadrao(), { estiloVisual: estilo ?? undefined });
    /*
     * A prévia mostra a receita **com os quadros sugeridos**: como nada vem
     * marcado por padrão, o modelo sem eles mostraria uma página de dia vazia — e
     * a amostra existe para mostrar justamente o que aquela agenda tem de próprio.
     * O miolo que a pessoa recebe abre limpo, com a sugestão a um clique no passo
     * *Quadros do dia*; o cartão diz isso logo abaixo da amostra.
     */
    const documento = construirDocumento({
      ...config,
      quadros: quadrosDaLista(modelo.quadrosSugeridos),
    });
    const indice = indiceDaPagina(documento.paginas);

    // Só as duas páginas de amostra ficam na memória: o documento inteiro
    // (centenas de páginas por modelo) é descartado aqui mesmo.
    const daPagina = documento.paginas[indice];
    /*
     * A capa é procurada pelo **tipo** da página, e não pela posição: sem capa
     * (o padrão de fábrica desde 27/09/2026), a primeira página do miolo é a folha
     * de rosto ou a visão do ano, e o botão "Capa" mostraria outra coisa.
     */
    const daCapa = documento.paginas.find((pagina) => pagina.tipo === 'CAPA');
    const amostras: Record<Vista, AmostraDoModelo | null> = {
      CAPA: daCapa ? { pagina: daCapa, lado: 'ESQUERDO' } : null,
      PAGINA: daPagina
        ? { pagina: daPagina, lado: indice % 2 === 0 ? 'ESQUERDO' : 'DIREITO' }
        : null,
    };

    return {
      config,
      resumo: documento.resumo,
      amostras,
      paleta: construirPaleta(config.cores),
    };
  }, [gerar, modelo, estilo]);

  return (
    <article
      id={`modelo-${modelo.id}`}
      className="cartao-tom grid gap-4 rounded-xl border p-4 transition-transform hover:-translate-y-0.5 sm:grid-cols-[minmax(0,210px)_1fr]"
      style={{ '--tom': tomDaCategoria(modelo.categoria) } as React.CSSProperties}
    >
      <div className="rounded-lg border border-border bg-muted/30 p-2">
        {pronto ? (
          <Amostra config={pronto.config} paleta={pronto.paleta} amostras={pronto.amostras} />
        ) : (
          <div className="flex h-[240px] flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border text-[11px] text-muted-foreground">
            <Layers className="size-4" />
            montando a prévia...
          </div>
        )}
      </div>

      <div className="flex min-w-0 flex-col">
        <header className="space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold tracking-tight">{modelo.nome}</h3>
            <Badge tom="neutro">
              <span
                className="mr-1 size-1.5 rounded-full"
                style={{ background: tomDaCategoria(modelo.categoria) }}
              />
              {ROTULO_CATEGORIA[modelo.categoria]}
            </Badge>
          </div>
          <p className="text-sm leading-relaxed text-muted-foreground">{modelo.resumo}</p>
          <p className="text-[11px] text-muted-foreground">
            {pronto ? (
              <>
                {pronto.config.formato} · {pronto.resumo.totalPaginas} páginas ·{' '}
                {pronto.config.cadernos.length} caderno(s) ·{' '}
                {pronto.config.calendario.modo === 'DATADO' ? `datada em ${pronto.config.calendario.ano}` : 'permanente'}
              </>
            ) : (
              'montando a prévia...'
            )}
          </p>
        </header>

        <ul className="mt-3 flex-1 space-y-1.5">
          {modelo.destaques.slice(0, 4).map((destaque) => (
            <li key={destaque} className="flex gap-2 text-xs leading-relaxed">
              <span
                className="mt-1.5 size-1 shrink-0 rounded-full"
                style={{ background: `color-mix(in oklab, ${tomDaCategoria(modelo.categoria)} 75%, transparent)` }}
              />
              <span>{destaque}</span>
            </li>
          ))}
        </ul>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {bloqueado ? (
            <Button tamanho="pequeno" disabled title="Seu acesso está bloqueado.">
              <Sparkles />
              Personalizar este modelo
            </Button>
          ) : (
            <Button asChild tamanho="pequeno">
              <Link to={`/criar?modelo=${encodeURIComponent(modelo.id)}${estilo ? `&estilo=${estilo}` : ''}`}>
                <Sparkles />
                Personalizar este modelo
              </Link>
            </Button>
          )}
          <span className="text-[11px] text-muted-foreground">
            Abre o assistente com este modelo aplicado — formato, cor, período e cadernos você ajusta
            nos passos. Os quadros do dia da amostra entram com um clique, no passo *Quadros do dia*.
          </span>
        </div>
      </div>
    </article>
  );
}

/**
 * Modelos prontos.
 *
 * Área do site para escolher e personalizar os modelos: cada cartão mostra uma
 * prévia real do miolo (gerada pelo mesmo motor da impressão), o que vem pronto
 * e o botão que abre o assistente já com o modelo aplicado.
 */
export function Modelos() {
  const { bloqueado } = useAcesso();
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState<Filtro>('TODAS');
  /**
   * Acabamento aplicado a todos os modelos da galeria.
   *
   * Nulo é "o acabamento que o modelo já traz": cada receita nasceu com a sua
   * combinação de tipografia e ornamento, e forçar um estilo sem o usuário pedir
   * tiraria a identidade de quem escolheu. Escolhendo um estilo, a prévia inteira
   * muda e o assistente abre já com ele.
   */
  const [estilo, setEstilo] = useState<EstiloVisual | null>(null);

  const grupos = useMemo(() => modelosPorCategoria(), []);
  const idsDosModelos = useMemo(() => MODELOS.map((modelo) => modelo.id), []);
  const previas = useFilaDePrevias(idsDosModelos);

  const filtrados = useMemo(() => {
    const termo = semAcento(busca.trim());
    return grupos
      .filter(({ categoria }) => filtro === 'TODAS' || categoria === filtro)
      .map(({ categoria, modelos }) => ({
        categoria,
        modelos: termo
          ? modelos.filter((modelo) =>
              semAcento([modelo.nome, modelo.resumo, ...modelo.destaques].join(' ')).includes(termo),
            )
          : modelos,
      }))
      .filter((grupo) => grupo.modelos.length > 0);
  }, [busca, filtro, grupos]);

  const encontrados = filtrados.reduce((total, grupo) => total + grupo.modelos.length, 0);

  return (
    <div className="space-y-8 py-4">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-2">
          <Badge tom="principal">
            <Sparkles className="size-3" />
            {MODELOS.length} receitas completas
          </Badge>
          <h1 className="text-3xl font-semibold tracking-tight">Modelos prontos</h1>
          <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Cada modelo já vem com formato, período, cores, tema e a lista de cadernos — e sugere os
            quadros do dia, que você marca num clique. Veja a prévia, personalize nos passos do
            assistente e imprima — ou comece do zero, sem passar por nenhum modelo.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="contorno" tamanho="grande">
            <Link to="/criar">
              <Eraser />
              Começar do zero
            </Link>
          </Button>
        </div>
      </header>

      {/*
        Os modelos de **texto** (ata, relatório, carta, ofício...) são outro
        produto e moram no editor de textos. Quem chega aqui procurando "modelo"
        merece o atalho em vez de descobrir por acaso.
      */}
      <p className="rounded-lg border border-border bg-muted/40 p-3 text-xs leading-relaxed text-muted-foreground">
        Procurando modelo de <strong className="text-foreground">ata, relatório, carta ou ofício</strong>? Esses
        ficam em{' '}
        <Link to="/textos" className="font-medium text-primary hover:underline">
          Meus textos
        </Link>{' '}
        — prontos para preencher em poucos cliques.
      </p>

      <section className="space-y-2 rounded-xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-sm font-semibold">Estilo visual</h2>
          <div className="flex flex-wrap gap-1">
            {[null, ...ESTILOS_VISUAIS].map((valor) => (
              <button
                key={valor ?? 'DO_MODELO'}
                type="button"
                onClick={() => setEstilo(valor as EstiloVisual | null)}
                className={cn(
                  'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                  estilo === valor
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:border-primary/40',
                )}
              >
                {valor ? ROTULO_ESTILO_VISUAL[valor as EstiloVisual] : 'Do modelo'}
              </button>
            ))}
          </div>
        </div>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {estilo
            ? `${DESCRICAO_ESTILO_VISUAL[estilo]} Vale para todos os ${MODELOS.length} modelos: é só o acabamento, o conteúdo de cada um continua igual.`
            : 'Cada modelo abre com o acabamento que já vem nele. Escolha um estilo para ver a galeria inteira com a mesma cara — e o assistente abre assim.'}
        </p>
      </section>

      {bloqueado ? (
        <p className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 text-sm text-amber-700 dark:text-amber-400">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          Seu acesso está bloqueado: você pode ver os modelos, mas não criar nem personalizar. Fale com o
          administrador em <Link to="/planos" className="underline">Planos</Link> para liberar de novo.
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={busca}
            onChange={(evento) => setBusca(evento.target.value)}
            placeholder="Buscar por nome, profissão ou assunto (ex.: dentista, viagem, bíblia)"
            className="pl-8"
          />
        </div>
        <Badge tom="neutro">
          {encontrados} de {MODELOS.length}
        </Badge>
      </div>

      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => setFiltro('TODAS')}
          className={cn(
            'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
            filtro === 'TODAS'
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-border text-muted-foreground hover:border-primary/40',
          )}
        >
          Todas
        </button>
        {grupos.map(({ categoria, modelos }) => (
          <button
            key={categoria}
            type="button"
            onClick={() => setFiltro(categoria)}
            className={cn(
              'flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
              filtro === categoria
                ? 'cartao-tom text-foreground'
                : 'border-border text-muted-foreground hover:border-primary/40',
            )}
            style={{ '--tom': tomDaCategoria(categoria) } as React.CSSProperties}
          >
            {/* O ponto de cor é o que liga a etiqueta ao cartão da categoria. */}
            <span className="size-1.5 rounded-full" style={{ background: tomDaCategoria(categoria) }} />
            {ROTULO_CATEGORIA[categoria]} ({modelos.length})
          </button>
        ))}
      </div>

      {encontrados === 0 ? (
        <p className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Nenhum modelo com esse termo. Tente outra palavra ou volte para{' '}
          <button
            type="button"
            className="underline"
            onClick={() => {
              setBusca('');
              setFiltro('TODAS');
            }}
          >
            todos os modelos
          </button>
          .
        </p>
      ) : (
        <div className="space-y-10">
          {filtrados.map(({ categoria, modelos }) => (
            <section key={categoria} className="space-y-4">
              <div className="flex items-center gap-3">
                <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  {ROTULO_CATEGORIA[categoria]}
                </h2>
                <Badge tom="neutro">{modelos.length}</Badge>
              </div>
              <div className="grid gap-4 xl:grid-cols-2">
                {modelos.map((modelo) => (
                  <CartaoDoModelo
                    key={modelo.id}
                    modelo={modelo}
                    bloqueado={bloqueado}
                    estilo={estilo}
                    gerar={Boolean(previas[modelo.id])}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <p className="rounded-md border border-border bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground">
        Personalizar um modelo <strong className="text-foreground">substitui</strong> formato, período, cores,
        tema, quadros e cadernos. Seus dados pessoais e as imagens que você já enviou são mantidos — e você
        pode mudar tudo de novo nos passos seguintes.
      </p>
    </div>
  );
}
